import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { emotions } from '../data/mock';
import { Waves, Crown, Sparkles, Heart, Wind } from 'lucide-react';

const emotionIcons = {
  serenite: Waves,
  privilege: Crown, 
  anticipation: Sparkles,
  intimite: Heart,
  liberte: Wind
};

const EmotionalFilters = ({ selectedEmotion, onEmotionChange, className = "" }) => {
  const [hoveredEmotion, setHoveredEmotion] = useState(null);

  const getEmotionStyle = (emotionId, isSelected, isHovered) => {
    const emotion = emotions[emotionId];
    
    if (isSelected) {
      return {
        background: `linear-gradient(135deg, ${emotion.colors[0]} 0%, ${emotion.colors[1]} 100%)`,
        color: emotionId === 'serenite' || emotionId === 'liberte' ? '#2C2C2C' : '#FFFFFF',
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 25px rgba(212, 175, 55, 0.3)`,
        border: '2px solid rgba(212, 175, 55, 0.5)'
      };
    }
    
    if (isHovered) {
      return {
        background: `linear-gradient(135deg, ${emotion.colors[0]}CC 0%, ${emotion.colors[1]}CC 100%)`,
        color: emotionId === 'serenite' || emotionId === 'liberte' ? '#2C2C2C' : '#FFFFFF',
        transform: 'translateY(-1px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)'
      };
    }
    
    return {
      background: 'rgba(255, 255, 255, 0.9)',
      color: '#4A5568',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    };
  };

  return (
    <div className={`flex flex-wrap gap-3 justify-center ${className}`}>
      {/* All Emotions Button */}
      <Button
        variant="outline"
        onClick={() => onEmotionChange(null)}
        className={`px-6 py-3 rounded-full transition-all duration-300 backdrop-blur-sm ${
          !selectedEmotion 
            ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white border-gray-700 transform -translate-y-1 shadow-lg' 
            : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white/90'
        }`}
        style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: '500',
          letterSpacing: '0.025em'
        }}
      >
        Toutes les Émotions
      </Button>

      {/* Individual Emotion Filters */}
      {Object.entries(emotions).map(([key, emotion]) => {
        const IconComponent = emotionIcons[key];
        const isSelected = selectedEmotion === key;
        const isHovered = hoveredEmotion === key;
        
        return (
          <Button
            key={key}
            variant="outline" 
            onClick={() => onEmotionChange(key)}
            onMouseEnter={() => setHoveredEmotion(key)}
            onMouseLeave={() => setHoveredEmotion(null)}
            className="px-6 py-3 rounded-full transition-all duration-300 border-0 relative group"
            style={{
              ...getEmotionStyle(key, isSelected, isHovered),
              fontFamily: 'Inter, sans-serif',
              fontWeight: '500',
              letterSpacing: '0.025em'
            }}
          >
            <div className="flex items-center gap-2">
              <IconComponent className="w-4 h-4" />
              <span>{emotion.label}</span>
            </div>
            
            {/* Subtle glow effect on hover */}
            {(isSelected || isHovered) && (
              <div 
                className="absolute inset-0 rounded-full opacity-20 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at center, ${emotion.colors[1]} 0%, transparent 70%)`,
                  animation: isSelected ? 'pulse 2s infinite' : 'none'
                }}
              />
            )}
          </Button>
        );
      })}
      
      {/* Emotional Description */}
      {selectedEmotion && (
        <div className="w-full mt-4 text-center">
          <p 
            className="text-sm text-gray-600 italic font-light max-w-md mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {emotions[selectedEmotion].description}
          </p>
        </div>
      )}
    </div>
  );
};

export default EmotionalFilters;